import {Box, Grid, SvgIcon, ToggleButton, ToggleButtonGroup, Typography, styled, useTheme, css} from '@mui/material';
import {
  formattedPropertyModel,
  useLocationQuery,
  utils,
  DlMuiDynamicIcon,
  TranslateUtils,
  DateUtils,
  NoDataMessageWithIcon,
  GetStylesFunction,
} from '@wallaby-ui/core';
import * as React from 'react';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {useSizeObserver} from '../../../hooks';
import {getLineChartItems, LightChart, LightChartLayout, LightLineChartTrace, useChartDateTimeStops, useFormatters} from 'src/js/common';
import {HaidiSchema, WallabyCoreSchema} from 'src/js/common/types';

type EventData = WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto'];
// "Display type" remains CRP/WBC/PCT for UI continuity; lookup against response data uses V3 method codes
type DisplayType = 'CRP' | 'WBC' | 'PCT';
const DATA_KEY_MAP: Record<DisplayType, EventData['eventType']> = {
  CRP: 'CRP_SP',
  WBC: 'WBC_B',
  PCT: 'PCT_SP',
};
type StaticChartProps = {
  color: string;
  selectedColor: string;
};
type ChartProps = StaticChartProps & {
  eventType: DisplayType;
  eventData: EventData | undefined;
  disabled: boolean;
};
function getChartProps(splitData: EventData[], eventType: DisplayType): ChartProps {
  const STYLE_MAP: Record<DisplayType, StaticChartProps> = {
    CRP: {
      color: '#6FA9FF',
      selectedColor: '#D8E8FF',
    },
    WBC: {
      color: '#07DE5D',
      selectedColor: '#CDFBE0',
    },
    PCT: {
      color: '#FF8717',
      selectedColor: '#FFDACA',
    },
  };
  const eventData = splitData.find((v) => v.eventType === DATA_KEY_MAP[eventType]);
  return {
    ...STYLE_MAP[eventType],
    eventType,
    eventData,
    disabled: (eventData?.dataPoints.length ?? 0) === 0,
  };
}

interface OwnProps {
  splitData?: EventData[];
  chartRange: [string | undefined, string | undefined];
  encounterData?: HaidiSchema.components['schemas']['EncounterPropertiesDto'];
  encounterId: string;
}

type Props = OwnProps;
export const CrpInTimeChartBox: React.FC<Props> = ({splitData, chartRange, encounterData, encounterId}) => {
  const intl = useIntl();
  const [query, changeFilters] = useLocationQuery(`/encounter/${encounterId}`, true);
  const filters = React.useMemo(() => {
    return {
      selectedChartType: query.selectedChartType ?? undefined,
    };
  }, [query]);

  const theme = useTheme();
  const styles = getStyles({theme});

  const crpProps = useMemo(() => getChartProps(splitData ?? [], 'CRP'), [splitData]);
  const wbcProps = useMemo(() => getChartProps(splitData ?? [], 'WBC'), [splitData]);
  const pctProps = useMemo(() => getChartProps(splitData ?? [], 'PCT'), [splitData]);
  const selectedChartType = useMemo(() => {
    if (filters.selectedChartType) return filters.selectedChartType;
    if (!crpProps.disabled) return 'CRP';
    if (!wbcProps.disabled) return 'WBC';
    if (!pctProps.disabled) return 'PCT';
    return null;
  }, [filters.selectedChartType, crpProps, wbcProps, pctProps]);
  const setSelectedChartCallback = React.useCallback(
    (newValue) => {
      changeFilters({
        selectedChartType: newValue,
      });
    },
    [changeFilters]
  );

  const propDefinition = useMemo(() => {
    return new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DATE,
    });
  }, []);
  const formattedAdmissionDateValue = formattedPropertyModel.FormattedProperty.formatValue(
    propDefinition,
    encounterData?.encounterStartedAt,
    intl
  );
  const formattedDischargeDateValue = formattedPropertyModel.FormattedProperty.formatValue(
    propDefinition,
    encounterData?.encounterEndedAt,
    intl
  );

  const labResultTranslation = utils.TranslateUtils.translate('encounter.chart.crp.title', intl);
  const downloadImageTitle = encounterData
    ? `${labResultTranslation} - ${encounterData.fullName} (${encounterData.identificationNumber}) - ${
        encounterData.admissionDepartment
      }, ${formattedAdmissionDateValue ?? ''} ${formattedDischargeDateValue ? `- ${formattedDischargeDateValue}` : ''}`
    : undefined;

  const selectedChartProps = useMemo(() => {
    const PROPS_MAP: Record<string, ChartProps> = {CRP: crpProps, WBC: wbcProps, PCT: pctProps};
    return PROPS_MAP[selectedChartType] ?? crpProps;
  }, [crpProps, wbcProps, pctProps, selectedChartType]);
  const formatters = useFormatters();
  const chartData: LightLineChartTrace[] = useMemo(() => {
    const {formatDateTime, formatFloat} = formatters;
    // Data points may arrive out of chronological order; sort ascending by timestamp so the
    // connecting line doesn't jump backwards to an earlier measurement.
    const sortedDataPoints = [...(selectedChartProps.eventData?.dataPoints ?? [])].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    return getLineChartItems(
      sortedDataPoints,
      [{color: selectedChartProps.color, label: selectedChartProps.eventType, size: 8}],
      (v) => ({
        x: v.timestamp,
        y: v.value,
        tooltip: `(${formatDateTime(v.timestamp)}, ${formatFloat(v.value)})`,
      })
    ).map((v) => ({...v, cliponaxis: false}));
  }, [selectedChartProps, formatters]);
  const chartDateTimeStops = useChartDateTimeStops();
  const chartLayout = useMemo(() => {
    const [chartStartDate, chartEndDate] = chartRange;
    const chartEndDateNextMidnight = DateUtils.nextMidnightIsoString(chartEndDate ?? '');
    const layout: LightChartLayout = {
      title: {
        text: `[${selectedChartProps.eventData?.unit ?? ''}]`,
        font: {
          size: 14,
        },
        xref: 'paper',
        x: 0,
      },
      margin: {
        t: 8,
        b: 42,
        l: 62,
        r: 40,
        pad: 4,
      },
      xaxis: {
        type: 'date',
        ...chartDateTimeStops,
        range: [chartStartDate ?? '', chartEndDateNextMidnight],
      },
      yaxis: {
        autorange: true,
        rangemode: 'tozero',
        fixedrange: false,
      },
    };
    return layout;
  }, [selectedChartProps, chartDateTimeStops, chartRange]);
  return (
    <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap', gap: 8}}>
      <ToggleButtonGroup
        orientation='vertical'
        value={selectedChartType}
        onChange={(_, val) => {
          setSelectedChartCallback(val);
        }}
        exclusive
        style={{
          border: 'unset !important',
          height: '100%',
          gap: 8,
        }}
      >
        {useChartButton({chartProps: crpProps})}
        {useChartButton({chartProps: wbcProps})}
        {useChartButton({chartProps: pctProps})}
      </ToggleButtonGroup>
      <div
        style={{
          width: 0,
          height: '100%',
          flex: 1,
        }}
      >
        {!selectedChartProps.disabled ? (
          <div style={{width: '100%', height: '100%', border: '1px solid #e2e8f2', borderRadius: 8, overflow: 'hidden'}}>
            <LightChart
              key={JSON.stringify(chartLayout.xaxis.range)}
              data={chartData}
              layout={chartLayout}
              config={{}}
              imageDownload={{
                title: {
                  text: downloadImageTitle ?? '',
                },
              }}
            />
          </div>
        ) : (
          <NoDataMessageWithIcon message={'hai.encounter.chart.crpInTime.noData'} inputCss={styles.wrapper} isHaidi />
        )}
      </div>
    </div>
  );
};

const getStyles: GetStylesFunction = () => ({
  wrapper: css`
    max-width: 410px;
  `,
});

type ChartButtonProps = {
  chartProps: ChartProps;
};
// NOTE: ToggleButtonGroup does not support wrapped button components: https://github.com/mui/material-ui/issues/21026
function useChartButton({chartProps}: ChartButtonProps): JSX.Element {
  const {color, selectedColor, disabled, eventType} = chartProps;
  const StyledToggleButton = styled(ToggleButton)({
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: selectedColor,
    },
    '&.Mui-disabled': {
      backgroundColor: '#F4F6FA',
      fill: '#9BA8BD',
    },
    'flex': 1,
  });
  const colorOrDisabledColor = disabled ? '#9BA8BD' : color;
  const {setRef: btnRef, height: btnHeight} = useSizeObserver({
    height: 100,
    width: 100,
  });
  const renderIcon = (btnHeight ?? 0) > 59;
  return (
    <StyledToggleButton
      ref={btnRef}
      value={eventType}
      aria-label={eventType}
      style={{
        width: '110px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: '#E2E8F2',
        flex: 1,
        height: 0,
      }}
      disabled={disabled}
    >
      <Grid container justifyContent='center' alignItems='center'>
        {renderIcon && (
          <Grid item xs={12}>
            <SvgIcon style={{width: '96px'}} viewBox='0 0 72 24'>
              <rect y='4' width='64' height='2' fill={colorOrDisabledColor} />
              <circle cx='32' cy='5' r='5' fill={colorOrDisabledColor} />
            </SvgIcon>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography color={colorOrDisabledColor}>{eventType}</Typography>
        </Grid>
      </Grid>
    </StyledToggleButton>
  );
}
